import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from "@angular/common";
import { StorageService } from "./storage.service";
import { AuthApiModel } from "../../api/models/auth.api.model";
import { plainToClass, plainToInstance } from "class-transformer";
import { CurrentUserViewModel } from "../models/current-user.view-model";
import { BehaviorSubject, EMPTY, from, Observable, of, Subject } from "rxjs";
import { concatMap, map, mergeMap, shareReplay, take, tap } from "rxjs/operators";
import { CredentialsApiModel } from "../../api/models/credentials.api.model";
import { AuthenticationRestService } from "../../api/services/authentication.rest.service";
import { NgxRolesService } from "ngx-permissions";
import { MediaObjectRestService } from "../../api/services/media-object.rest.service";
import { MediaObjectApiModel } from "../../api/models/media-object.api-model";
import { MediaObjectViewModel } from "../models/media-object.view-model";
import { QueryParamsApiModel } from "../../api/models/query-params-api.model";
import { FilterParameterApiModel } from "../../api/models/filter-parameter.api.model";
import { ResultListApiModel } from "../../api/models/result-list.api.model";
import { Oauth2UserApiModel } from "../../api/models/oauth2-user.api.model";
import { FacebookService } from "./facebook.service";
import { UserApiModel } from "../../api/models/user.api-model";
import { FacebookLoginResponse } from "@capgo/capacitor-social-login/dist/esm/definitions";
import { GoogleLoginResult, GoogleService } from "./google.service";
import { JwtService } from "./jwt.service";
import { TokenApiModel } from "../../api/models/token.api.model";
import { REQUEST } from "../providers/http-context-providers";
import type { Request } from 'express';
import {CacheStoreService} from "./cache-store.service";
import {EventSourceService} from "./event-source.service";

@Injectable({
  providedIn: 'root'
})
export class TokenDataProviderService {

  get currentUser(): Observable<CurrentUserViewModel> {
    if (!this.isLoggedInSSR() && !this.isLoggedIn()) {
      return of(new CurrentUserViewModel(new UserApiModel()));
    }
    const hasApiId = !!this.userSource.getValue()['@id'];
    if (this.forceRefresh) {
      if (!this.currentUserRefresh$) {
        this.currentUserRefresh$ = this.refreshUser().pipe(
          tap(() => {
            this.forceRefresh = false;
          }),
          shareReplay(1)
        );
      }
      return this.currentUserRefresh$;
    }
    if (hasApiId || this.hasFetchedCurrentUser) {
      return this.user$;
    }
    if (!this.currentUserRefresh$) {
      this.currentUserRefresh$ = this.refreshUser().pipe(
        tap(() => {
          this.hasFetchedCurrentUser = true;
        }),
        shareReplay(1)
      );
    }
    return this.currentUserRefresh$;

  }

  get oAuth2User(): Oauth2UserApiModel {
    return plainToClass(Oauth2UserApiModel, this.getJwtUser());
  }

  set currentUser(currentUserViewModelObservable: Observable<CurrentUserViewModel>) {
    currentUserViewModelObservable.subscribe((currentUserViewModel: CurrentUserViewModel) => {
      this.userSource.next(currentUserViewModel);
      this.hasFetchedCurrentUser = true;
    });
  }

  private userSource = new BehaviorSubject<CurrentUserViewModel>(new CurrentUserViewModel(this.getJwtUser()));
  public user$: Observable<CurrentUserViewModel>;
  private readonly sessionChangedSource = new Subject<void>();
  public readonly sessionChanged$ = this.sessionChangedSource.asObservable();
  private currentUserRefresh$?: Observable<CurrentUserViewModel>;
  private hasFetchedCurrentUser = false;
  private forceRefresh = false;

  constructor(
    private jwt: JwtService,
    private storage: StorageService,
    private as: AuthenticationRestService,
    private mo: MediaObjectRestService,
    private rs: NgxRolesService,
    private fs: FacebookService,
    private gs: GoogleService,
    private cacheStore: CacheStoreService,
    private eventSourceService: EventSourceService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request?: Request
  ) {
    this.user$ = this.userSource.asObservable();
  }

  public getJwtUser(): UserApiModel {
    if (isPlatformBrowser(this.platformId)) {
      const jwtToken = this.storage.getItem('token');
      if (jwtToken) {
        const data = this.jwt.decodeToken(jwtToken);
        const user = plainToInstance(UserApiModel, data);
        this.rs.flushRoles();
        this.loadRoles(user);
        return user;
      }
    }
    return new UserApiModel();
  }

  public isLoggedIn(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const hasValidToken = !this.jwt.isTokenExpired();
      if (hasValidToken) {
        return true;
      }

      if (!this.jwt.hasRemember()) {
        return false;
      }

      return !!this.storage.getItem('refresh_token');
    }

    return false;
  }

  public isAuthenticatedFully(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return this.isLoggedIn() && !!this.getJwtUser().completedAt && !!this.getJwtUser().enabled;
    }

    return false;
  }

  public requiresRegistrationCompletion(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const user = this.getJwtUser();

      return this.isLoggedIn()
        && !user.completedAt
        && !user.username;
    }

    return false;
  }

  public isEnabled(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return this.isLoggedIn() && !!this.getJwtUser().enabled;
    }

    return false;
  }

  public login(credentials: CredentialsApiModel): Observable<AuthApiModel> {
    return this.as.login(credentials)
      .pipe(tap((response: AuthApiModel) => {
        this.jwt.setSession(response, credentials.remember);
        this.handleSessionChange();
        this.userSource.next(new CurrentUserViewModel(this.getJwtUser()))
      }));
  }

  public loginFromInvitation(token: TokenApiModel): Observable<AuthApiModel> {
    return this.as.loginFromInvitation(token)
      .pipe(tap((response: AuthApiModel) => {
        this.jwt.setSession(response, false);
        this.handleSessionChange();
        void this.cacheStore.deleteByPrefix('/api/notifications');
        void this.cacheStore.deleteByPrefix('/api/projects');
        this.userSource.next(new CurrentUserViewModel(this.getJwtUser()))
      }));
  }

  public loginFromOwnerChangeInvitation(token: TokenApiModel): Observable<AuthApiModel> {
    return this.as.loginFromOwnerChangeInvitation(token)
      .pipe(tap((response: AuthApiModel) => {
        this.jwt.setSession(response, false);
        this.handleSessionChange();
        void this.cacheStore.deleteByPrefix('/api/notifications');
        void this.cacheStore.deleteByPrefix('/api/projects');
        this.userSource.next(new CurrentUserViewModel(this.getJwtUser()))
      }));
  }

  public fbLogin(credentials: CredentialsApiModel, limited: boolean) {
    return from(this.fs.loginWithFacebook(limited))
      .pipe(
        concatMap(({ result }) => {
          if (!result) return EMPTY;
          console.log({ limited });
          return of(result);
        }),
        concatMap((fbLoginResponse: FacebookLoginResponse) => this.as.fbLogin({ limitedLogin: limited, payload: fbLoginResponse })),
        tap((response: AuthApiModel) => {
          this.jwt.setSession(response, credentials.remember);
          this.handleSessionChange();
          this.userSource.next(new CurrentUserViewModel(this.getJwtUser()))
        })
      );
  }

  public googleLogin(credentials: CredentialsApiModel, limited: boolean = true) {
    return from(this.gs.login(limited))
      .pipe(
        concatMap(({ result }: { result: GoogleLoginResult }) => {
          if (!result) return EMPTY;
          return of(result);
        }),
        concatMap((googleLoginResponse: GoogleLoginResult) => this.as.googleLogin({ limitedLogin: limited, payload: googleLoginResponse })),
        tap((response: AuthApiModel) => {
          this.jwt.setSession(response, credentials.remember);
          this.handleSessionChange();
          this.userSource.next(new CurrentUserViewModel(this.getJwtUser()))
        })
      );
  }

  private handleSessionChange(): void {
    this.forceRefresh = true;
    this.hasFetchedCurrentUser = false;
    this.currentUserRefresh$ = undefined;
    this.sessionChangedSource.next();
  }

  public refreshUser(): Observable<CurrentUserViewModel> {
    return this.as.getCurrentUser()
      .pipe(
        map((userApiModel: UserApiModel) => {
          const currentUserVM = new CurrentUserViewModel(userApiModel);
          this.loadRoles(userApiModel);
          this.userSource.next(currentUserVM);
          this.hasFetchedCurrentUser = true;
          const params = new QueryParamsApiModel();
          if (userApiModel.avatar) {
            params.filters.push(new FilterParameterApiModel('id', userApiModel.avatar));
          }
          if (userApiModel.background) {
            params.filters.push(new FilterParameterApiModel('id', userApiModel.background));
          }
          if (params.filters.length) {
            this.mo.getMediaObjectCollection(params)
              .pipe(take(1))
              .subscribe((mediaObjectResultList: ResultListApiModel<MediaObjectApiModel>) => {
                const avatar = mediaObjectResultList.records.find(e => e["@id"] === userApiModel.avatar);
                const background = mediaObjectResultList.records.find(e => e["@id"] === userApiModel.background);
                currentUserVM.avatar = new MediaObjectViewModel(avatar);
                currentUserVM.background = new MediaObjectViewModel(background);
                this.userSource.next(currentUserVM);
              });
          }
          return currentUserVM;
        }),
        mergeMap((currentUserVM: CurrentUserViewModel) => of(currentUserVM))
      );
  }

  private loadRoles(userApiModel: UserApiModel): void {
    userApiModel.roles.forEach((e) => this.rs.addRole(e, []));
  }

  public logout(): void {
    this.jwt.clearAllTokenRelatedFromStorage();
    this.rs.flushRoles();
    this.hasFetchedCurrentUser = false;
    this.currentUserRefresh$ = undefined;
    this.forceRefresh = false;
    this.userSource.next(new CurrentUserViewModel(new UserApiModel()));
    this.sessionChangedSource.next();
    void this.cacheStore.clear();
    this.eventSourceService.closeAll();
    //this.fs.logout();
  }

  public isLoggedWithRememberOption(): boolean {
    return this.jwt.hasRemember();
  }

  getTokenFromSSR(): string | null {
    const cookie = this.request?.headers?.cookie;
    if (!cookie) return null;

    return cookie.match(/AUTH_TOKEN=([^;]+)/)?.[1] ?? null;
  }

  public isLoggedInSSR(): boolean {
    return !!this.getTokenFromSSR();
  }
}
